import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Native swipe via CSS scroll-snap; arrows on desktop; no autoplay.
export function SlideViewer({ slides, title }) {
  const track = useRef(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const el = track.current;
    const onScroll = () => setIdx(Math.round(el.scrollLeft / el.clientWidth));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  const go = (i) => {
    const el = track.current;
    const n = Math.max(0, Math.min(slides.length - 1, i));
    el.scrollTo({ left: n * el.clientWidth, behavior: "smooth" });
  };
  const onKey = (e) => { if (e.key === "ArrowRight") go(idx + 1); if (e.key === "ArrowLeft") go(idx - 1); };
  return (
    <div className="relative" data-testid="slide-viewer" tabIndex={0} onKeyDown={onKey} aria-roledescription="carousel" aria-label={title}>
      <div ref={track} className="flex snap-x snap-mandatory overflow-x-auto rounded-[10px] border border-border bg-[#F1EDE4] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="slide-track">
        {slides.map((s, i) => (
          <div key={i} className="w-full flex-shrink-0 snap-center" aria-label={`Slide ${i + 1} of ${slides.length}`} data-testid={`slide-${i}`}>
            {s.image_url
              ? <img src={s.image_url} alt={s.text || `${title} — slide ${i + 1}`} className="aspect-[4/5] w-full object-contain" loading={i < 2 ? "eager" : "lazy"} draggable={false} />
              : <div className="flex aspect-[4/5] w-full items-center justify-center p-8 text-center font-heading text-xl font-bold text-ink">{s.text}</div>}
          </div>
        ))}
      </div>
      <button onClick={() => go(idx - 1)} disabled={idx === 0} aria-label="Previous slide" data-testid="slide-prev"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-white/90 text-ink shadow-sm transition-opacity hover:bg-white disabled:opacity-0 md:flex">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button onClick={() => go(idx + 1)} disabled={idx >= slides.length - 1} aria-label="Next slide" data-testid="slide-next"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-white/90 text-ink shadow-sm transition-opacity hover:bg-white disabled:opacity-0 md:flex">
        <ChevronRight className="h-5 w-5" />
      </button>
      <div className="mt-3 flex items-center justify-center gap-1.5" data-testid="slide-dots">
        {slides.map((_, i) => (
          <button key={i} onClick={() => go(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`slide-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-accent" : "w-1.5 bg-border"}`} />
        ))}
        <span className="ml-2 font-mono text-xs text-muted" data-testid="slide-counter">{idx + 1}/{slides.length}</span>
      </div>
    </div>
  );
}
